import { env } from '@config/env';

/**
 * WHERE the auth tokens live — the single source of truth for it (2026-08-17).
 *
 * The user's report: *"every day I need to login again with user and password, why?"* Because every
 * token, **the refresh token included**, was written to `sessionStorage`, which the browser wipes when
 * it closes. `automaticSilentRenew` was on and working the whole time; it simply had nothing to renew
 * from the next morning. The refresh token was never expiring — it was being deleted.
 *
 * The fix is a per-device choice rather than a blanket one, because this app holds financial data and
 * "stay signed in" is exactly the wrong default on a shared machine:
 *
 *   remembered  -> localStorage   (survives a browser restart; renew resumes silently)
 *   otherwise   -> sessionStorage (today's behaviour: the session dies with the tab)
 *
 * **The FLAG itself must live in localStorage.** Storing "should I persist?" in the store it selects
 * would be circular — it would vanish exactly when it is needed, on the next cold boot, and every
 * user would silently fall back to the daily-login behaviour they asked us to fix.
 */
const REMEMBER_KEY = 'journal.auth.remember';

/** Session-scoped keys that must never outlive a sign-out, in EITHER store. */
const OIDC_PREFIX = 'oidc.';
/** amazon-cognito-identity-js writes its own token cache under this prefix — it holds a refresh
 *  token too, so a sign-out that only cleared `oidc.*` left a live credential behind. */
const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

/** Whether this device keeps the session. ON unless the device explicitly opted out (2026-09-13
 *  user decision — it defaulted to off from 2026-08-17 until then): a device with no recorded
 *  choice persists; only an untick, stored as `'0'`, does not. */
export function isRemembered(): boolean {
  try {
    return window.localStorage.getItem(REMEMBER_KEY) !== '0';
  } catch {
    // Safari private mode and locked-down profiles throw on storage access. localStorage that
    // cannot be read cannot hold tokens either, so degrade to session-only instead of breaking
    // sign-in entirely.
    return false;
  }
}

/**
 * Record the choice. MUST be called BEFORE the tokens are written, because `authStore()` reads it to
 * decide where they go — the login form sets it, then bridges the session. An opt-out is WRITTEN
 * (`'0'`), never expressed by removing the key: an absent key means the default, which is on.
 */
export function setRemembered(remember: boolean): void {
  try {
    window.localStorage.setItem(REMEMBER_KEY, remember ? '1' : '0');
  } catch {
    // Non-fatal: sign-in still works, the session just won't persist.
  }
}

/** The store the tokens belong in, per this device's choice. */
export function authStore(): Storage {
  return isRemembered() ? window.localStorage : window.sessionStorage;
}

/** The oidc-client-ts user key, derived the same way the library derives it. */
export function oidcUserKey(): string {
  return `oidc.user:${env.cognitoAuthority}:${env.cognitoClientId}`;
}

/**
 * Drop every auth artefact from BOTH stores.
 *
 * Both, always — not just the active one. The stores diverge the moment someone toggles the
 * checkbox: signing in "remembered", signing out, then signing in again unticked would otherwise
 * leave the previous `localStorage` refresh token sitting there, outliving the session that was
 * supposed to be session-scoped. Sign-out and sign-in both call this, so neither store can carry a
 * credential the user believes they discarded.
 */
export function clearAuthStorage(): void {
  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      for (const key of Object.keys(store)) {
        if (key.startsWith(OIDC_PREFIX) || key.startsWith(COGNITO_PREFIX)) store.removeItem(key);
      }
    } catch {
      // Ignore an inaccessible store — the other one still gets cleared.
    }
  }
}
